import React from 'react'
import { useTranslation } from 'react-i18next'
import { Award, Lock } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { imageUrl } from '@/lib/images'
import { cn } from '@/lib/utils'
import type { BadgeVisibility, MemberBadge } from '@/types/badges'
import { BadgeOrigin } from './BadgeOrigin'

/** What a chip needs: a ranking entry has no description nor visibility; a grant has both and its origin. */
export interface BadgeLike {
  id: string
  name: string
  imageUrl: string
  description?: string | null
  visibility?: BadgeVisibility
}

/* `card`: the quick profile card's achievement row (36 px). `header`: the member page's, 32 px on a phone and 44 px from `sm` up. */
const SIZES = { xs: 'h-5 w-5', sm: 'h-7 w-7', card: 'h-9 w-9', md: 'h-10 w-10', header: 'h-8 w-8 sm:h-11 sm:w-11', lg: 'h-16 w-16', xl: 'h-24 w-24' } as const
const PIXELS = { xs: 40, sm: 56, card: 72, md: 80, header: 88, lg: 128, xl: 192 } as const

/** The badge's picture (a PNG up to 512 px, transparency kept), or a neutral medal while it has none. */
export const BadgeArt = ({ src, size = 'md', className }: { src?: string | null; size?: keyof typeof SIZES; className?: string }) => {
  const [broken, setBroken] = React.useState(false)
  React.useEffect(() => setBroken(false), [src])
  if (!src || broken) {
    return (
      <span className={cn('inline-flex flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-gray-400', SIZES[size], className)} aria-hidden>
        <Award className="h-1/2 w-1/2" />
      </span>
    )
  }
  return (
    <img
      src={imageUrl(src, { w: PIXELS[size], fit: 'max' }) || src}
      alt=""
      loading="lazy"
      onError={() => setBroken(true)}
      className={cn('flex-shrink-0 object-contain', SIZES[size], className)}
    />
  )
}

/** "Privado": only the person, the owner and the admins see it. */
export const PrivateMark = ({ className }: { className?: string }) => {
  const { t } = useTranslation('badges')
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full bg-gray-100 px-1.5 py-0.5 text-[11px] font-medium text-gray-600', className)} title={t('visibility.privateHint')}>
      <Lock className="h-3 w-3" aria-hidden />
      {t('visibility.private')}
    </span>
  )
}

/**
 * One badge: the picture (and the name, when `showName`), with the details on
 * hover or tap (a popover, so it works on touch too). A private badge carries
 * the lock, since only its holder and the owner and admins ever get it.
 */
export const BadgeChip = ({ badge, grant, size = 'sm', showName = false, className }: {
  badge: BadgeLike
  /** The grant, when known: its origin and date go in the details. */
  grant?: MemberBadge
  size?: keyof typeof SIZES
  showName?: boolean
  className?: string
}) => {
  const { t, i18n } = useTranslation('badges')
  const isPrivate = badge.visibility === 'private'
  const awardedOn = grant?.awardedAt
    ? new Intl.DateTimeFormat(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' }).format(new Date(grant.awardedAt))
    : ''
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          title={badge.description ? `${badge.name}: ${badge.description}` : badge.name}
          aria-label={t('chip.open', { name: badge.name })}
          data-badge-chip={badge.id}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            'relative inline-flex min-w-0 items-center gap-1.5 rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary',
            showName && 'border border-gray-200 bg-white py-0.5 pl-0.5 pr-2.5 hover:border-gray-300',
            className
          )}
        >
          <BadgeArt src={badge.imageUrl} size={size} />
          {showName && <span className="min-w-0 truncate text-xs font-medium text-gray-800">{badge.name}</span>}
          {isPrivate && (
            showName
              ? <Lock className="h-3 w-3 flex-shrink-0 text-gray-400" aria-label={t('visibility.private')} />
              : <span className="absolute -bottom-0.5 -right-0.5 rounded-full bg-white p-px text-gray-500 shadow-sm"><Lock className="h-2.5 w-2.5" aria-label={t('visibility.private')} /></span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3">
          <BadgeArt src={badge.imageUrl} size="lg" />
          <div className="min-w-0 flex-1 space-y-1">
            <p className="break-words text-sm font-semibold text-gray-900">{badge.name}</p>
            {isPrivate && <PrivateMark />}
            {badge.description && <p className="whitespace-pre-line break-words text-xs text-gray-600">{badge.description}</p>}
          </div>
        </div>
        {grant && (
          <div className="mt-3 border-t border-gray-100 pt-2 text-xs text-gray-500">
            {/* Origin and date on their own lines, so a long origin never breaks the date in half. */}
            <p className="break-words"><BadgeOrigin grant={grant} /></p>
            {awardedOn && <p className="whitespace-nowrap">{awardedOn}</p>}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}

/** A row of badges, the first `max` and a "+N" for the rest (a button to `onMore`, when given). */
export const BadgeRow = ({ badges, max = 6, size = 'sm', onMore, className }: {
  badges: Array<{ badge: BadgeLike; grant?: MemberBadge }>
  max?: number
  size?: keyof typeof SIZES
  onMore?: () => void
  className?: string
}) => {
  const { t } = useTranslation('badges')
  if (badges.length === 0) return null
  const rest = badges.length - max
  const big = size !== 'xs' && size !== 'sm'
  const moreClass = big
    ? cn('inline-flex flex-shrink-0 items-center justify-center rounded-full bg-gray-100 text-xs font-semibold text-gray-600 sm:text-sm', SIZES[size])
    : 'text-xs font-medium text-gray-500'
  return (
    <div className={cn('flex flex-wrap items-center gap-1', className)} data-badge-row>
      {badges.slice(0, max).map(({ badge, grant }) => <BadgeChip key={grant?.id ?? badge.id} badge={badge} grant={grant} size={size} />)}
      {rest > 0 && (onMore
        ? (
          <button type="button" onClick={onMore} title={t('chip.more', { count: rest })} aria-label={t('chip.more', { count: rest })} data-badge-more
            className={cn(moreClass, 'transition-colors hover:bg-gray-200 hover:text-gray-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary', !big && 'rounded px-1')}>
            +{rest}
          </button>
        )
        : <span className={moreClass} title={t('chip.more', { count: rest })} data-badge-more>+{rest}</span>)}
    </div>
  )
}
